<script setup lang="ts">
import { ref } from 'vue';
import { ArrowRight, Clock3, MapPin, Menu, Phone, Star, UtensilsCrossed, X } from 'lucide-vue-next';
import '@/assets/styles/home.css';
import heroImage from '@/assets/images/jadedrache-hero.png';

const mobileOpen = ref(false);
const category = ref('Vorspeisen');
const menu = {
  Vorspeisen: [
    ['Edamame', 'Meersalz · gerösteter Sesam', '6'],
    ['Gemüse Gyoza', 'Chinakohl · Ingwer · Soja', '9'],
    ['Lachs Tataki', 'Ponzu · Sesam · Frühlingslauch', '16'],
    ['Jade Bao', 'Schweinebauch · Gurke · Hoisin', '12'],
    ['Crispy Tofu', 'Szechuan-Mayo · Kräuter', '10'],
    ['Tuna Tatar', 'Avocado · Yuzu · Reiscracker', '17'],
  ],
  Hauptgerichte: [
    ['Jade Ramen', 'Miso-Brühe · Pak Choi · Ei · Chiliöl', '18'],
    ['Knusprige Ente', 'Pflaume · Wokgemüse · Jasminreis', '24'],
    ['Black Pepper Beef', 'Rinderfilet · Paprika · schwarzer Pfeffer', '27'],
    ['Miso Lachs', 'Wilder Brokkoli · Dashi · Sesamreis', '26'],
    ['Korean Fried Chicken', 'Gochujang · Frühlingslauch · Pickles', '21'],
    ['Jade Curry', 'Kokos · Zitronengras · Gemüse · Reis', '20'],
    ['Peking Ente', 'Mandarin-Pfannkuchen · Gurke · Hoisin', '29'],
    ['Teriyaki Hähnchen', 'Sesam · Brokkoli · Jasminreis', '21'],
    ['Szechuan Rind', 'Chili · Knoblauch · Paprika · Reis', '25'],
    ['Char Siu Pork', 'Honigglasur · Pak Choi · Duftreis', '23'],
    ['Tonkotsu Ramen', 'Schweinebauch · Ei · Nori · Bambus', '21'],
    ['Shoyu Ramen', 'Hähnchen · Soja-Brühe · Mais · Ei', '19'],
    ['Yaki Udon', 'Rindfleisch · Udon · Wokgemüse', '22'],
    ['Pad Thai', 'Garnelen · Reisnudeln · Erdnuss · Limette', '22'],
    ['Red Thai Duck', 'Rote Currysauce · Ananas · Thai-Basilikum', '25'],
    ['Garnelen Tempura', 'Dashi · Rettich · Sesamreis', '24'],
    ['Wasabi Tuna', 'Thunfischsteak · Wasabi-Kartoffel · Ponzu', '29'],
    ['Seafood Laksa', 'Garnelen · Muscheln · Kokos · Reisnudeln', '26'],
    ['Bulgogi Bowl', 'Mariniertes Rind · Kimchi · Ei · Reis', '23'],
    ['Five Spice Chicken', 'Fünf-Gewürze · Wokgemüse · Reis', '22'],
  ],
  Vegetarisch: [
    ['Mapo Tofu', 'Szechuanpfeffer · Pilze · Reis', '17'],
    ['Miso Aubergine', 'Sesam · Frühlingslauch · Reis', '18'],
    ['Grünes Thai Curry', 'Kokos · Zuckerschoten · Thai-Basilikum', '19'],
    ['Dan Dan Noodles', 'Sesam · Pak Choi · Chili · Erdnuss', '17'],
    ['Wokgemüse', 'Ingwer · Soja · Cashew · Reis', '16'],
  ],
  Sushi: [
    ['Jadedrache Roll', 'Lachs · Avocado · Gurke · Tobiko', '18'],
    ['Spicy Tuna Roll', 'Thunfisch · Chili · Frühlingslauch', '17'],
    ['Crunchy Veggie Roll', 'Avocado · Spargel · Tempura', '15'],
    ['Sashimi Selection', 'Lachs · Thunfisch · Hamachi, 12 Stück', '29'],
    ['Nigiri Set', 'Auswahl des Küchenchefs, 8 Stück', '24'],
  ],
  Desserts: [
    ['Matcha Tiramisu', 'Mascarpone · Kakao · Matcha', '9'],
    ['Mango Mochi', 'Kokos · Limette', '8'],
    ['Sesam Eis', 'Schwarzer Sesam · Karamell', '8'],
    ['Yuzu Cheesecake', 'Weißer Sesam · Yuzu-Gel', '10'],
  ],
  Getränke: [
    ['Jasmin Eistee', 'Hausgemacht · Limette · Minze', '6'],
    ['Lychee Spritz', 'Lychee · Yuzu · Soda, alkoholfrei', '8'],
    ['Jade Highball', 'Japanese Whisky · Ingwer · Soda', '12'],
    ['Sake Selection', 'Junmai Ginjo, 0,1 l', '9'],
    ['Grüner Tee', 'Sencha oder Jasmin, Kännchen', '5'],
  ]
};
const close = () => mobileOpen.value = false;
</script>

<template>
  <main>
    <header class="nav-shell">
      <a class="brand" href="#start" @click="close"><span class="brand-mark">J</span><span>JADEDRACHE<small>ASIAN KITCHEN</small></span></a>
      <nav :class="{ open: mobileOpen }" aria-label="Hauptnavigation">
        <a href="#story" @click="close">Unsere Küche</a><a href="#speisekarte" @click="close">Speisekarte</a><a href="#besuch" @click="close">Besuch</a>
        <a href="tel:+496111234567" class="nav-cta" @click="close"><Phone/> 0611 123 45 67</a>
      </nav>
      <button class="menu-toggle" aria-label="Menü öffnen" @click="mobileOpen = !mobileOpen"><X v-if="mobileOpen"/><Menu v-else/></button>
    </header>

    <section id="start" class="hero" :style="{ backgroundImage: `linear-gradient(90deg, rgba(2,13,29,.98) 0%, rgba(2,13,29,.82) 42%, rgba(2,13,29,.15) 75%), url(${heroImage})` }">
      <div class="hero-inner">
        <p class="eyebrow"><span></span> Moderne asiatische Küche · Wiesbaden</p>
        <h1>Asien im Herzen.<br><em>Blau in der Seele.</em></h1>
        <p class="hero-copy">Feine Aromen, handverlesene Zutaten und ehrliches Handwerk – serviert in einer Atmosphäre zwischen Shanghai und Wiesbaden.</p>
        <div class="hero-actions"><a href="tel:+496111234567" class="btn primary"><Phone/> 0611 123 45 67</a><a href="#speisekarte" class="btn ghost">Speisekarte entdecken</a></div>
        <div class="hero-meta"><span><Star/> 4,8 bei unseren Gästen</span><span><Clock3/> Di–So ab 17 Uhr</span></div>
      </div>
    </section>

    <section id="story" class="story section-pad">
      <div class="story-label">01 <span></span> UNSERE PHILOSOPHIE</div>
      <div><h2>Tradition trifft<br><em>neue Perspektive.</em></h2><p>Unsere Küche verbindet die aromatische Vielfalt Ostasiens mit einer modernen, leichten Handschrift. Hausgemachte Brühen, lokale Produkte und Gewürze, die ihre eigene Geschichte erzählen.</p><p>Wir kochen nicht einfach Gerichte. Wir schaffen einen Abend, an den man sich erinnert.</p><div class="signature">蒼龍 <span>KÜCHENCHEF LIN</span></div></div>
      <div class="story-card"><span>SEIT 2018</span><strong>6</strong><p>Jahre voller Geschmack, Begegnungen und gemeinsamer Abende.</p></div>
    </section>

    <section id="speisekarte" class="menu-section section-pad">
      <div class="section-heading"><p class="eyebrow dark"><span></span> UNSERE SPEISEKARTE</p><h2>Einmal quer durch<br><em>unsere Küche.</em></h2><p class="menu-intro">Zum Teilen, Entdecken oder ganz für sich allein – alle Gerichte werden frisch für Sie zubereitet.</p></div>
      <div class="tabs"><button v-for="(_, name) in menu" :key="name" :class="{active: category === name}" @click="category = name">{{ name }}</button></div>
      <div class="dish-grid">
        <article v-for="(dish, index) in menu[category as keyof typeof menu]" :key="dish[0]" class="dish"><span>0{{ index + 1 }}</span><div><h3>{{ dish[0] }}</h3><p>{{ dish[1] }}</p></div><strong>{{ dish[2] }} €</strong></article>
      </div>
      <p class="menu-note"><UtensilsCrossed/> Alle Gerichte können auf Wunsch in ihrer Schärfe angepasst werden.</p>
    </section>

    <section class="experience"><div><p class="eyebrow"><span></span> DER ABEND GEHÖRT IHNEN</p><h2>Mehr als ein Essen.<br><em>Ein Erlebnis.</em></h2><p>Gedämpftes Licht, tiefe Blautöne und der Duft frisch gerösteter Gewürze. Ob erstes Date, Familienabend oder besonderer Anlass – bei uns darf die Zeit kurz stillstehen.</p><a href="tel:+496111234567">Jetzt telefonisch reservieren <Phone/></a></div><div class="quote"><Star/><p>„Ein Ort, an dem jedes Detail stimmt – vom ersten Tee bis zum letzten Bissen.“</p><span>— MARA K. · WIESBADEN</span></div></section>

    <section id="besuch" class="visit section-pad"><div class="section-heading"><p class="eyebrow dark"><span></span> IHR BESUCH</p><h2>Wir freuen uns<br><em>auf Sie.</em></h2></div><div class="visit-grid"><div><MapPin/><h3>Adresse</h3><p>Taunusstraße 24<br>65183 Wiesbaden</p><a href="https://maps.google.com" target="_blank" rel="noopener">Route anzeigen →</a></div><div><Clock3/><h3>Öffnungszeiten</h3><p>Di–Sa: 17:00–23:00<br>So: 17:00–22:00<br>Montag Ruhetag</p></div><div><Phone/><h3>Kontakt</h3><p>0611 123 45 67<br>hallo@jadedrache.de</p><a href="tel:+496111234567">Direkt anrufen →</a></div></div></section>

    <section id="reservieren" class="reservation phone-reservation"><div><p class="eyebrow"><span></span> TISCHRESERVIERUNG</p><h2>Ein Anruf genügt.</h2><p>Rufen Sie uns während der Öffnungszeiten an. Wir bestätigen Ihren Wunschtermin direkt persönlich.</p></div><a class="phone-card" href="tel:+496111234567"><span><Phone/></span><small>RESERVIERUNG UNTER</small><strong>0611 123 45 67</strong><em>Jetzt anrufen <ArrowRight/></em></a></section>

    <footer><div class="brand"><span class="brand-mark">J</span><span>JADEDRACHE<small>ASIAN KITCHEN</small></span></div><p>© {{ new Date().getFullYear() }} Jadedrache · Demo-Website</p><div><router-link to="/impressum">Impressum</router-link><router-link to="/datenschutz">Datenschutz</router-link></div></footer>
  </main>
</template>
